import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, Download, Trash2, X, Folder, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatBytes = (b) => {
  if (!b) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const ExpiryBadge = ({ days }) => {
  if (days === null || days === undefined) return null;
  if (days < 0) return <span className="text-[10px] bg-red-900/40 text-red-300 border border-red-700 px-1.5 py-0.5 rounded">Expired {Math.abs(days)}d ago</span>;
  if (days <= 30) return <span className="text-[10px] bg-amber-900/40 text-amber-300 border border-amber-700 px-1.5 py-0.5 rounded">Expires in {days}d</span>;
  if (days <= 90) return <span className="text-[10px] bg-yellow-900/40 text-yellow-300 border border-yellow-700 px-1.5 py-0.5 rounded">Renew in {days}d</span>;
  return <span className="text-[10px] bg-green-900/40 text-green-300 border border-green-700 px-1.5 py-0.5 rounded">Valid · {days}d</span>;
};

export default function ComplianceDocuments({ canUpload }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [showUpload, setShowUpload] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ category: 'GST', title: '', license_no: '', issued_on: '', expires_on: '', notes: '', file: null });

  const fetchData = async () => {
    try {
      const [d, c] = await Promise.all([
        axios.get(`${API}/compliance/documents`, { withCredentials: true }),
        axios.get(`${API}/compliance/categories`, { withCredentials: true }),
      ]);
      setDocs(d.data || []);
      setCategories(c.data?.categories || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(
    () => filter === 'All' ? docs : docs.filter(d => d.category === filter),
    [docs, filter]
  );

  const grouped = useMemo(() => {
    const acc = {};
    for (const d of docs) acc[d.category] = (acc[d.category] || 0) + 1;
    return acc;
  }, [docs]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file) { toast.error('Pick a file first'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('category', form.category);
      fd.append('title', form.title);
      fd.append('license_no', form.license_no);
      fd.append('issued_on', form.issued_on);
      fd.append('expires_on', form.expires_on);
      fd.append('notes', form.notes);
      await axios.post(`${API}/compliance/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setShowUpload(false);
      setForm({ category: 'GST', title: '', license_no: '', issued_on: '', expires_on: '', notes: '', file: null });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setSubmitting(false); }
  };

  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(`${API}/compliance/documents/${doc.id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = doc.file_name; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const deleteDoc = async (doc) => {
    if (!window.confirm(`Delete "${doc.title}"? This permanently removes the file.`)) return;
    try {
      await axios.delete(`${API}/compliance/documents/${doc.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (loading) return <div className="text-zinc-400 animate-pulse text-sm">Loading documents...</div>;

  return (
    <div className="space-y-4" data-testid="compliance-docs-section">
      {/* Folder pills */}
      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={() => setFilter('All')} data-testid="folder-all"
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${filter === 'All' ? 'bg-amber-500 text-black font-bold' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
          <Folder size={12} /> All ({docs.length})
        </button>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} data-testid={`folder-${c.toLowerCase().replace(/\s+/g, '-')}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-colors ${filter === c ? 'bg-amber-500 text-black font-bold' : 'bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200'}`}>
            <Folder size={12} /> {c} {grouped[c] ? `(${grouped[c]})` : ''}
          </button>
        ))}
        <div className="flex-1" />
        {canUpload && (
          <button data-testid="upload-doc-btn" onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
            <Upload size={12} /> Upload Document
          </button>
        )}
      </div>

      {/* Document grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 bg-zinc-900 border border-dashed border-zinc-800 rounded-xl">
          <FileText className="mx-auto text-zinc-700" size={32} />
          <p className="text-zinc-500 text-sm mt-2">No documents in {filter === 'All' ? 'any folder' : `the ${filter} folder`}</p>
          {canUpload && <p className="text-zinc-600 text-[11px] mt-1">Click "Upload Document" to add your first license/certificate.</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(d => (
            <div key={d.id} data-testid={`doc-card-${d.id}`}
              className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 hover:border-amber-500/40 transition-colors flex flex-col gap-2">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-500/10 text-amber-400 flex items-center justify-center flex-shrink-0">
                  <FileText size={14} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{d.category}</span>
                    {d.days_to_expiry !== null && d.days_to_expiry < 30 && d.days_to_expiry >= 0 && <AlertTriangle size={11} className="text-amber-400" />}
                    {d.days_to_expiry !== null && d.days_to_expiry < 0 && <AlertTriangle size={11} className="text-red-400" />}
                    {d.days_to_expiry !== null && d.days_to_expiry >= 30 && <CheckCircle2 size={11} className="text-green-400" />}
                  </div>
                  <h4 className="text-zinc-100 text-sm font-semibold truncate">{d.title}</h4>
                  {d.license_no && <p className="text-zinc-500 text-[10px] font-mono truncate">{d.license_no}</p>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-zinc-500">
                <ExpiryBadge days={d.days_to_expiry} />
                <span>{formatBytes(d.size_bytes)}</span>
              </div>
              {(d.issued_on || d.expires_on) && (
                <div className="text-[10px] text-zinc-600">
                  {d.issued_on && <span>Issued {d.issued_on}</span>}
                  {d.expires_on && <span> · Expires {d.expires_on}</span>}
                </div>
              )}
              {d.notes && <p className="text-[10px] text-zinc-500 line-clamp-2">{d.notes}</p>}
              <div className="flex items-center gap-1 mt-auto pt-1 border-t border-zinc-800/50">
                <button onClick={() => downloadDoc(d)} data-testid={`download-${d.id}`}
                  className="flex-1 flex items-center justify-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[11px] py-1.5 rounded-md transition-colors">
                  <Download size={11} /> Download
                </button>
                {canUpload && (
                  <button onClick={() => deleteDoc(d)} data-testid={`delete-${d.id}`}
                    className="p-1.5 rounded-md bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-800/30">
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 bg-black/70 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" onClick={() => setShowUpload(false)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md my-4 max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="upload-doc-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 flex-shrink-0">
              <h3 className="font-heading font-semibold text-zinc-50">Upload License / Certificate</h3>
              <button onClick={() => setShowUpload(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleUpload} className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Category *</label>
                  <select required value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500">
                    {categories.map(c => <option key={c} value={c} className="bg-zinc-900">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">License/Cert No</label>
                  <input value={form.license_no} onChange={e => setForm(f => ({ ...f, license_no: e.target.value }))}
                    placeholder="e.g. 24AABCT1234Z1Z5" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm font-mono focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Title *</label>
                  <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. GST Registration Certificate (HQ)" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Issued On</label>
                  <input type="date" value={form.issued_on} onChange={e => setForm(f => ({ ...f, issued_on: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Expires On</label>
                  <input type="date" value={form.expires_on} onChange={e => setForm(f => ({ ...f, expires_on: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                  <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    placeholder="Optional remarks" className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:outline-none focus:border-amber-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-zinc-400 text-xs mb-1 block">File *</label>
                  <input required type="file" accept=".pdf,.png,.jpg,.jpeg,.webp"
                    onChange={e => setForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                    data-testid="upload-file-input"
                    className="w-full text-zinc-300 text-xs file:mr-2 file:rounded-md file:border-0 file:bg-amber-500/20 file:text-amber-300 file:px-2 file:py-1.5 file:cursor-pointer" />
                  <p className="text-[10px] text-zinc-600 mt-1">Accepted: PDF, PNG, JPG, WEBP. Auth-protected — only logged-in users can download.</p>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowUpload(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="upload-submit"
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50">
                  {submitting ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
